import { ENV_VARS } from '@/env';
import { Col, Modal, type ModalProps, Row } from 'antd';
import i18n from 'i18next';
import type { ReactNode } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import Button from '../Buttons/Button';
import IconButton from '../Buttons/IconButton';
import SvgConverter from '../Common/SvgConverter';

interface CommonModalProps extends ModalProps {
  contentClassName?: string;
  headerClassName?: string;
  descriptionClassName?: string;
  okLoading?: boolean;
  customFooter?: ReactNode;
  customHeader?: ReactNode;
  okButtonId?: string;
  cancelButtonId?: string;
  footerClassName?: string;
  draggable?: boolean;
  isOkBtn?: boolean;
  isCancelBtn?: boolean;
  labelForTestId?: string;
  isHBUser?: boolean;
}

const CommonModal = ({
  open = false,
  title = 'Title',
  okText = i18n.t('translation:default.options.yes'),
  cancelText = i18n.t('translation:default.options.no'),
  onClose = () => {},
  onOk = () => {},
  onCancel = () => {},
  children,
  className = '',
  contentClassName = '',
  descriptionClassName = '',
  headerClassName = '',
  okLoading = false,
  customFooter = false,
  customHeader = undefined,
  destroyOnClose = false,
  maskClosable = false,
  okButtonId = 'common-modal-yes',
  cancelButtonId = 'common-modal-cancel',
  footerClassName = '',
  draggable = false,
  modalRender,
  afterOpenChange = () => {},
  forceRender = false,
  isOkBtn = true,
  isCancelBtn = true,
  labelForTestId,
  isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton',
}: CommonModalProps) => {
  return (
    <Modal
      afterOpenChange={afterOpenChange}
      destroyOnClose={destroyOnClose}
      className={`common-modal ${className}`}
      closable={false}
      centered
      footer={false}
      open={open} // Don't change to open until antd upgrade is done
      maskClosable={maskClosable}
      onCancel={onClose}
      {...(draggable ? { modalRender } : {})}
      forceRender={forceRender}
      data-testid={labelForTestId ? labelForTestId : ''}
    >
      <Row
        className={`header-section ${headerClassName} ${isHBUser ? 'hb-bg-color' : ''}`}
        justify="space-between"
        align="middle"
      >
        {!customHeader ? (
          <Col>
            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
            <label className="title">{title}</label>
          </Col>
        ) : (
          customHeader
        )}
        <Col className="lh-0" data-testid={labelForTestId ? `${labelForTestId}-close-button` : ''}>
          <IconButton onClick={onClose}>
            <SvgConverter Icon={CloseIcon} color="white" />
          </IconButton>
        </Col>
      </Row>
      <div className={`content-section ${contentClassName}`}>
        <div className={`description ${descriptionClassName}`}>{children}</div>
        {customFooter ? (
          customFooter
        ) : (
          <Row className={`footer-section ${footerClassName}`} justify="end">
            <Col>
              <Row gutter={20}>
                {isOkBtn && (
                  <Col>
                    <Button id={okButtonId} variant="success" onClick={onOk} loading={okLoading}>
                      {okText}
                    </Button>
                  </Col>
                )}
                {isCancelBtn && (
                  <Col>
                    <Button id={cancelButtonId} variant="danger" type="default" onClick={onCancel}>
                      {cancelText}
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        )}
      </div>
    </Modal>
  );
};

export default CommonModal;
